import { useCallback, useEffect, useRef, useState } from "react";
import { postVoucherVerify } from "../../APICalling/APICalling";
import { useGlobalContext } from "../../Context/GlobalContext";
import { showAppToast } from "../../Utils/appToast";
import { InputValidation } from "../../Utils/utils";
import CheckIcon from "../Icons/CheckIcon";
import VoucherVerifySuccessIcon from "../Icons/VoucherVerifySuccessIcon";
import VoucherVerifyErrorIcon from "../Icons/VoucherVerifyErrorIcon";
import WarningTriangleIcon from "../Icons/WarningTriangleIcon";
import TrashIcon from "../Icons/TrashIcon";
import {
	extractServiceIdsFromBooking,
	formatVoucherExpiryDate,
	getCashVoucherExpiryDate,
	getCashVoucherFaceValue,
	getCashVoucherId,
	getVoucherFromVerifyResponse,
	getVoucherVerifyErrorMessage,
	getVoucherVerifySuccessMessage,
	isCashVoucherGracePeriod,
	isVoucherVerifyResponseOk,
} from "../salesCheckout/voucherSaleHelpers";
import sidebarStyles from "./rightsidebar.module.css";
import styles from "./CashVoucherPaymentForm.module.css";

/**
 * Cash voucher entry + verify UI for payment confirmation.
 * Notifies parent via onChange when verify state changes.
 */
const CashVoucherPaymentForm = ({
	title = "Cash Voucher",
	bookingDetails = null,
	disabled = false,
	referenceError = "",
	onClearReferenceError,
	onClearParentError,
	onChange,
}) => {
	const {
		selectedOutlet,
		loggedOutletID,
		reference,
		setReference,
		customerBookingItems,
	} = useGlobalContext();

	const inputRef = useRef(null);
	const skipBlurRef = useRef(false);

	const [code, setCode] = useState("");
	const [verifyStatus, setVerifyStatus] = useState("idle");
	const [verifyMessage, setVerifyMessage] = useState("");
	const [verifiedVoucher, setVerifiedVoucher] = useState(null);
	const [redeemableInput, setRedeemableInput] = useState("");

	const faceValue =
		verifiedVoucher?._faceValue ?? getCashVoucherFaceValue(verifiedVoucher);
	const isVerified =
		verifyStatus === "success" &&
		Number.isFinite(faceValue) &&
		faceValue > 0 &&
		getCashVoucherId(verifiedVoucher) != null;

	const isGracePeriod = isVerified && Boolean(verifiedVoucher?._isGracePeriod);
	const graceExpiryLabel = isGracePeriod? formatVoucherExpiryDate(verifiedVoucher?._expiryDate): "";
	const redeemableAmount = Math.round(Number(redeemableInput) * 100) / 100;
	let redeemableError = "";
	if (isGracePeriod) {
		if (!redeemableInput || !Number.isFinite(redeemableAmount) || redeemableAmount <= 0) {
			redeemableError = "Enter a valid redeemable amount.";
		} else if (redeemableAmount > faceValue) {
			redeemableError = `Redeemable amount cannot exceed $${faceValue.toFixed(2)}.`;
		}
	}
	const gracePeriodReduction =isGracePeriod && !redeemableError? Math.round((faceValue - redeemableAmount) * 100) / 100: null;

	const canRedeem = isVerified && !redeemableError;
	const redeemValue = canRedeem ? (isGracePeriod ? redeemableAmount : faceValue) : null;

	const lastEmittedRef = useRef(null);

	useEffect(() => {
		const next = {
			isVerified: canRedeem,
			faceValue: redeemValue,
			code: String(verifiedVoucher?.code || code || "").trim(),
			verifiedVoucher: canRedeem ? verifiedVoucher : null,
			isGracePeriod,
			availableBalance: isVerified ? faceValue : null,
			gracePeriodReduction,
		};
		const prev = lastEmittedRef.current;
		const same =
			prev &&
			prev.isVerified === next.isVerified &&
			prev.faceValue === next.faceValue &&
			prev.code === next.code &&
			prev.verifiedVoucher === next.verifiedVoucher &&
			prev.isGracePeriod === next.isGracePeriod &&
			prev.availableBalance === next.availableBalance &&
			prev.gracePeriodReduction === next.gracePeriodReduction;
		if (same) return;
		lastEmittedRef.current = next;
		onChange?.(next);
	}, [
		canRedeem,
		redeemValue,
		code,
		verifiedVoucher,
		isGracePeriod,
		isVerified,
		faceValue,
		gracePeriodReduction,
		onChange,
	]);

	useEffect(() => {
		const id = requestAnimationFrame(() => {
			inputRef.current?.focus({ preventScroll: true });
		});
		return () => {
			cancelAnimationFrame(id);
			lastEmittedRef.current = null;
			onChange?.({
				isVerified: false,
				faceValue: null,
				code: "",
				verifiedVoucher: null,
			});
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps -- reset parent only on unmount
	}, []);

	const resetState = useCallback(() => {
		setCode("");
		setVerifyStatus("idle");
		setVerifyMessage("");
		setVerifiedVoucher(null);
		setRedeemableInput("");
	}, []);

	const verifySerial = useCallback(
		async (rawCode) => {
			const serial = String(rawCode || "").replace(/\s/g, "").trim();
			if (!serial) {
				setVerifyStatus("idle");
				setVerifyMessage("");
				setVerifiedVoucher(null);
				return;
			}

			const outletId = Number(selectedOutlet || loggedOutletID);
			if (!Number.isFinite(outletId) || outletId <= 0) {
				const msg = "Select an outlet before verifying vouchers.";
				setVerifyStatus("error");
				setVerifyMessage(msg);
				setVerifiedVoucher(null);
				showAppToast("error", msg);
				return;
			}

			setVerifyStatus("loading");
			setVerifyMessage("");
			setVerifiedVoucher(null);

			try {
				const service_ids = extractServiceIdsFromBooking(
					bookingDetails,
					customerBookingItems
				);
				const resp = await postVoucherVerify({
					serial_number: serial,
					outlet_id: outletId,
					panel: "outlet",
					voucher_category: "cash",
					...(service_ids.length > 0 ? { service_ids } : {}),
				});

				if (resp != null && typeof resp === "object" && isVoucherVerifyResponseOk(resp)) {
					const voucherPayload = getVoucherFromVerifyResponse(resp);
					const nextFaceValue = getCashVoucherFaceValue(voucherPayload);
					const voucherId = getCashVoucherId(voucherPayload);
					if (!Number.isFinite(nextFaceValue) || nextFaceValue <= 0 || voucherId == null) {
						const msg = "This voucher cannot be redeemed as a cash voucher.";
						setVerifyStatus("error");
						setVerifyMessage(msg);
						setVerifiedVoucher(null);
						showAppToast("error", msg);
						return;
					}
					const successMessage = getVoucherVerifySuccessMessage(resp);
					setVerifiedVoucher({
						...voucherPayload,
						id: voucherId,
						code: serial,
						_faceValue: nextFaceValue,
						_isGracePeriod: isCashVoucherGracePeriod(voucherPayload, resp),
						_expiryDate: getCashVoucherExpiryDate(voucherPayload, resp),
					});
					setRedeemableInput(nextFaceValue.toFixed(2));
					setVerifyStatus("success");
					setVerifyMessage(successMessage);
					showAppToast("success", successMessage);
					return;
				}

				const msg = getVoucherVerifyErrorMessage(resp);
				setVerifyStatus("error");
				setVerifyMessage(String(msg));
				setVerifiedVoucher(null);
				showAppToast("error", String(msg));
			} catch (err) {
				const msg =
					err?.response?.data?.message ||
					err?.message ||
					"Voucher verification failed";
				setVerifyStatus("error");
				setVerifyMessage(String(msg));
				setVerifiedVoucher(null);
				showAppToast("error", String(msg));
			}
		},
		[bookingDetails, customerBookingItems, loggedOutletID, selectedOutlet]
	);

	const handleCodeChange = (value) => {
		const sanitized = String(value || "").replace(/\s/g, "");
		setCode(sanitized);
		setVerifyStatus("idle");
		setVerifyMessage("");
		setVerifiedVoucher(null);
		onClearParentError?.();
	};

	const handleClear = () => {
		skipBlurRef.current = true;
		resetState();
		onClearParentError?.();
		requestAnimationFrame(() => {
			inputRef.current?.focus({ preventScroll: true });
		});
	};

	const handleBlur = (value) => {
		if (skipBlurRef.current) {
			skipBlurRef.current = false;
			return;
		}
		if (verifyStatus === "success" || verifyStatus === "loading") return;
		verifySerial(value);
	};

	const handleVerifyClick = () => {
		if (!code) {
			inputRef.current?.focus({ preventScroll: true });
			return;
		}
		verifySerial(code);
	};

	return (
		<div className={sidebarStyles.sidebarContainer}>
			<div className={styles.title}>
				<span className="text-capitalize">{String(title).toLowerCase()}</span>
			</div>
			<div className={styles.codeRow}>
				<div className={styles.inputShell}>
					<input
						ref={inputRef}
						type="text"
						className={`${styles.input} ${
							verifyStatus === "error" ? styles.inputError : ""
						}`}
						placeholder="Enter voucher code"
						value={code}
						disabled={disabled || verifyStatus === "loading"}
						onChange={(e) => handleCodeChange(e.target.value)}
						onBlur={(e) => handleBlur(e.target.value)}
						onKeyDown={(e) => {
							if (e.key !== "Enter") return;
							e.preventDefault();
							skipBlurRef.current = true;
							verifySerial(e.currentTarget.value);
						}}
					/>
					{verifyStatus === "success" && (
						<span
							className={styles.inputTick}
							title={isGracePeriod ? "Expired – grace period" : "Verified"}
						>
							{isGracePeriod ? <WarningTriangleIcon /> : <VoucherVerifySuccessIcon />}
						</span>
					)}
					{verifyStatus === "error" && (
						<button
							type="button"
							className={`${styles.inputTick} ${styles.inputIconBtn}`}
							title="Clear voucher"
							disabled={disabled}
							onMouseDown={(e) => e.preventDefault()}
							onClick={handleClear}
						>
							<VoucherVerifyErrorIcon />
						</button>
					)}
				</div>
				{verifyStatus === "success" || verifyStatus === "error" ? (
					<button
						type="button"
						className={styles.iconBtn}
						title="Clear voucher"
						disabled={disabled}
						onMouseDown={(e) => e.preventDefault()}
						onClick={handleClear}
					>
						<TrashIcon width={18} height={18} color="#767676" />
					</button>
				) : (
					<button
						type="button"
						className={styles.iconBtn}
						title="Verify voucher"
						disabled={disabled || verifyStatus === "loading"}
						onMouseDown={(e) => e.preventDefault()}
						onClick={handleVerifyClick}
					>
						<CheckIcon width={15} height={11} color="#767676" />
					</button>
				)}
			</div>
			{verifyStatus === "error" && verifyMessage ? (
				<p className={`${sidebarStyles.errorMessage} pl-0 mt-0`}>{verifyMessage}</p>
			) : null}
			{isGracePeriod ? (
				<div className={styles.graceSection}>
					<div className={styles.graceTitle}>Grace Period Redemption</div>
					<p className={styles.graceMessage}>
						{graceExpiryLabel
							? `This voucher has expired on ${graceExpiryLabel} and can be redeemed within its grace period.`
							: "This voucher has expired and can be redeemed within its grace period."}
					</p>
					<div className={styles.graceRow}>
						<span className={styles.graceLabel}>Available Voucher Balance</span>
						<span className={styles.graceValue}>${Number(faceValue).toFixed(2)}</span>
					</div>
					<div className={styles.graceRow}>
						<span className={styles.graceLabel}>Grace Period Reduction</span>
						<span className={styles.graceValue}>
							{gracePeriodReduction != null ? `$${gracePeriodReduction.toFixed(2)}` : "-"}
						</span>
					</div>
					<div className={styles.graceRow}>
						<span className={styles.graceLabel}>Redeemable Amount</span>
						<input
							type="text"
							inputMode="decimal"
							className={`${styles.redeemableInput} ${
								redeemableError ? styles.inputError : ""
							}`}
							value={`$${redeemableInput}`}
							disabled={disabled}
							aria-label="Redeemable amount"
							aria-invalid={redeemableError ? "true" : "false"}
							onFocus={(e) => e.target.select()}
							onChange={(e) => {
								const raw = e.target.value.replace(/^\$/, "");
								if (!/^\d*\.?\d{0,2}$/.test(raw)) return;
								const n = Number(raw);
								if (raw && Number.isFinite(n) && n > faceValue) return;
								setRedeemableInput(raw);
								onClearParentError?.();
							}}
							onBlur={() => {
								const n = Number(redeemableInput);
								if (redeemableInput && Number.isFinite(n)) {
									setRedeemableInput(n.toFixed(2));
								}
							}}
						/>
					</div>
					{redeemableError ? (
						<p className={`${sidebarStyles.errorMessage} ${styles.graceError}`}>
							{redeemableError}
						</p>
					) : null}
				</div>
			) : (
				<div className={sidebarStyles.sidebarRow}>
					<div className={`${sidebarStyles.sidebarCellLeft} ${sidebarStyles.sidebarText}`}>
						Voucher Value
					</div>
					<div className={`${sidebarStyles.sidebarCellRight} ${sidebarStyles.sidebarText}`}>
						{isVerified ? `$${Number(faceValue).toFixed(2)}` : "0"}
					</div>
				</div>
			)}
			<div className={sidebarStyles.sidebarCellLeft}>
				<p>Reference</p>
				<input
					type="text"
					className={styles.referenceInput}
					placeholder="Type here"
					value={reference}
					disabled={disabled}
					onFocus={(e) => {
						if (e.target.value) e.target.select();
					}}
					onChange={(e) => {
						const value = e.target.value;
						if (InputValidation.hasEmoji(value)) return;
						setReference(value);
						onClearReferenceError?.();
					}}
				/>
				{referenceError ? (
					<p className={sidebarStyles.errorMessage}>{referenceError}</p>
				) : null}
			</div>
		</div>
	);
};

export default CashVoucherPaymentForm;
